import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    BreadcrumbGroup,
    SpaceBetween,
    Button,
    ContentLayout,
    Box,
    Container,
    Header,
    FormField,
    Input,
    RadioGroup,
    Alert,
    Link,
    Textarea,
  ProgressBar,
  Spinner, Badge, Tabs,
  Table, StatusIndicator,
  ColumnLayout
} from '@amzn/awsui-components-console';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { getUserAtom } from '../../contexts/userContext';
import { makeApiCall } from '../../utils/api';
import { API_PATHS } from '../../config/api-endpoints';
import { useProfile } from '../../hooks/useProfiles';
import { jobUpdateSchema, validateWithSchema } from '../../utils/validationSchemas';
import JsonDataViewer from '../molecules/JsonDataViewer';
import { cleanMermaidContent } from '../../utils/cleanMermaidContent';
import NProgress from 'nprogress';
import { useFeedbackGeneration } from '../../hooks/useFeedbackGeneration';
import ComplianceQuestionnaireForm from '../chat/ComplianceQuestionnaireForm';
import ComplianceDetails from '../chat/ComplianceDetails';
import './JobCreateEdit.scss';

interface JobFormData {
    name: string;
    type: 'ondemand' | 'scheduled';
    scheduleFrequency?: 'monthly' | 'daily' | 'weekly' | 'custom';
    scheduleTime?: string;
    scheduleDate?: string;
    description?: string;
    templateFile?: File | null;
    templateId?: string;
    enabled: boolean;
    status?: 'ACTIVE' | 'INACTIVE' | 'DRAFT' | 'RELEASE';
}

interface JobDetails {
    jobId: string;
    jobName: string;
    description?: string;
    profileId: string;
    status: 'ACTIVE' | 'INACTIVE' | 'DRAFT' | 'RELEASE' | 'APPROVAL_REQUESTED' | 'APPROVED';
    templateId?: string;
    templateUri?: string;  // S3 URI of the template
    templateStatus?: string;  // Template validation status (deprecated)
    generateStatus?: string;  // Code generation status (SUBMITTED, GENERATING, COMPLETED, FAILED)
    mermaidDiagram?: string;  // Mermaid diagram from GetJob API
    aiSummary?: string;
    artifacts?: {  // S3 URIs for artifacts
        mermaid?: string;
        summary?: string;
        sql?: string;
    };
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    crId?: string;
    crUrl?: string;
    // SOX Compliance fields
    complianceSubmitted?: boolean;
    annualFinancialImpact?: string;
    isSox?: boolean;
    soxControlTypes?: string[];
    complianceSubmittedBy?: string;
    complianceSubmittedAt?: string;
    // Audit trail fields
    customerApprovedAt?: string;
    crReviewerName?: string;
    crApprovedAt?: string;
}

const JobCreateEdit: React.FC = () => {
    const { id: profileId, jobId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [authenticatedUser] = useAtom(getUserAtom);
    
    // Fetch profile data from API
    const { profile: profileData, loading: profileLoading, error: profileError } = useProfile(profileId || null);
    
    // Determine if we're in edit mode
    const isEditMode = Boolean(jobId);
    const pageTitle = isEditMode ? 'Edit Job' : 'Create Job';
    
    // Form state
    const [formData, setFormData] = useState<JobFormData>({
        name: '',
        type: 'ondemand',
        scheduleFrequency: 'monthly',
        scheduleTime: '00:00',
        scheduleDate: '',
        description: '',
        templateFile: null,
        templateId: '',
        enabled: true,
        status: 'DRAFT'
    });
    
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState<string | null>(null);
    const [templateUploaded, setTemplateUploaded] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [jobDetails, setJobDetails] = useState<JobDetails | null>(null);
    const [fetchedMermaidContent, setFetchedMermaidContent] = useState<string | null>(null);
    const [fetchedAiSummary, setFetchedAiSummary] = useState<string | null>(null);
    const [fetchedSqlContent, setFetchedSqlContent] = useState<string | null>(null);
    const [isRunningJob, setIsRunningJob] = useState(false);
    const [isSubmittingForReview, setIsSubmittingForReview] = useState(false);
    const [latestRun, setLatestRun] = useState<{ runId: string; status: string; startedAt: string; completedAt?: string } | null>(null);
    
    // Feedback generation hook
    const {
        feedbackText,
        setFeedbackText,
        wordCount,
        isGenerating: isFeedbackGenerating,
        generationStatus: feedbackGenerationStatus,
        generationProgress,
        submitFeedback,
        error: feedbackError,
        clearError: clearFeedbackError,
        successMessage: feedbackSuccessMessage,
        clearSuccessMessage: clearFeedbackSuccessMessage
    } = useFeedbackGeneration({
        jobId: jobId || '',
        onSuccess: (jobData) => {
            // Refresh the entire job details to get the latest status and artifacts
            fetchJobDetails();
        },
        onError: () => {
            // Also refresh on error to get the latest status
            fetchJobDetails();
        }
    });
    
    // Helper function to get feedback status message
    const getFeedbackStatusMessage = (status: string): string => {
        switch(status) {
            case 'generating':
                return 'Processing your feedback and regenerating code...';
            case 'polling':
                return 'Checking code regeneration status...';
            case 'success':
                return 'Code successfully regenerated with your feedback!';
            case 'failed':
                return 'Code regeneration failed. Please try again.';
            default:
                return 'Preparing to regenerate code...';
        }
    };
    
    // Fetch artifact content from S3 URI
    const fetchArtifactFromS3 = useCallback(async (s3Uri: string): Promise<string | null> => {
        try {
            const { getPresignedDownloadUrl } = await import('../../utils/s3Download');
            const match = s3Uri.match(/^s3:\/\/[^\/]+\/(.+)$/);
            if (!match || !match[1]) return null;

            const urlResponse = await getPresignedDownloadUrl(match[1]);
            if (!urlResponse?.downloadUrl) return null;

            const response = await fetch(urlResponse.downloadUrl, {
                mode: 'cors',
                headers: { 'Accept': 'text/plain, */*' }
            });
            if (!response.ok) return null;

            return await response.text();
        } catch (e: any) {
            console.warn('Failed to fetch artifact from S3:', e.message || e);
            return null;
        }
    }, []);
    
    // Function to fetch job details
    const fetchJobDetails = useCallback(async () => {
            if (!isEditMode || !jobId || !authenticatedUser) return;
            
            setLoading(true);
            setApiError(null);
            NProgress.start();
            
            try {
                const response = await makeApiCall(
                    API_PATHS.JOBS.GET,
                    'POST',
                    {
                        userId: authenticatedUser.username,
                        jobId: jobId
                    }
                );
                
                if (response.job) {
                    const job = response.job;
                    setJobDetails(job);
                    
                    // Check if the job has schedules to determine type
                    let jobType: 'ondemand' | 'scheduled' = 'ondemand';
                    try {
                        const schedulesResponse = await makeApiCall(
                            API_PATHS.SCHEDULES.LIST,
                            'POST',
                            { jobId, limit: 1 }
                        );
                        if (schedulesResponse.schedules && schedulesResponse.schedules.length > 0) {
                            jobType = 'scheduled';
                        }
                    } catch {
                        // If schedules API fails, default to ondemand
                    }
                    
                    // Map job details to form data
                    setFormData({
                        name: job.jobName || '',
                        type: jobType,
                        scheduleFrequency: 'monthly',
                        scheduleTime: '00:00',
                        scheduleDate: '',
                        description: job.description || '',
                        templateFile: null,
                        templateId: job.templateId || '',
                        enabled: job.status === 'ACTIVE' || job.status === 'RELEASE',
                        status: job.status
                    });
                    
                    if (job.templateUri) {
                        setTemplateUploaded(true);
                    }

                    // Fetch latest job run
                    try {
                        const runsResponse = await makeApiCall(
                            API_PATHS.JOBS.LIST_RUNS,
                            'POST',
                            { jobId, limit: 1 }
                        );
                        if (runsResponse.jobRuns && runsResponse.jobRuns.length > 0) {
                            setLatestRun(runsResponse.jobRuns[0]);
                        }
                    } catch {
                        // Ignore - latest run is optional
                    }

                    // Fetch artifacts from S3 (mermaid, summary, sql)
                    const artifacts = job.artifacts;
                    if (artifacts) {
                        if (artifacts.mermaid) {
                            const content = await fetchArtifactFromS3(artifacts.mermaid);
                            setFetchedMermaidContent(content ? cleanMermaidContent(content) : null);
                        }
                        if (artifacts.summary) {
                            const content = await fetchArtifactFromS3(artifacts.summary);
                            setFetchedAiSummary(content);
                        }
                        if (artifacts.sql) {
                            const content = await fetchArtifactFromS3(artifacts.sql);
                            setFetchedSqlContent(content);
                        }
                    }
                }
            } catch (error: any) {
                console.error('Failed to fetch job details:', error);
                setApiError(error.message || 'Failed to load job details');
            } finally {
                setLoading(false);
                NProgress.done();
            }
    }, [isEditMode, jobId, authenticatedUser, fetchArtifactFromS3]);
        
    // Load existing job data if in edit mode
    useEffect(() => {
        fetchJobDetails();
    }, [fetchJobDetails]);
    
    // Validate form with Zod
    const validateForm = (): boolean => {
        const validationData = {
            name: formData.name,
            description: formData.description,
            type: formData.type,
        };
        
        const result = validateWithSchema(jobUpdateSchema, validationData);
        
        if (!result.success) {
            setErrors(result.errors);
            return false;
        }
        
        // Additional custom validations
        const newErrors: Record<string, string> = {};
        
        if (!isEditMode && !templateUploaded) {
            newErrors.template = 'Template file is required';
        }
        
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return false;
        }
        
        setErrors({});
        return true;
    };
    
    // Handle form submission
    const handleSubmit = async () => {
 
        
        if (!validateForm()) {
            return;
        }
        
        setSaving(true);
        setApiError(null);
        NProgress.start();
        
        try {
        
            
            if (isEditMode && jobId) {
                // Update existing job using EditJob API
                const updatePayload = {
                    jobId,
                    jobName: formData.name,
                    description: formData.description || ''
                };
                
                const response = await makeApiCall(API_PATHS.JOBS.EDIT, 'POST', updatePayload);
                setShowSuccessAlert(true);
                
                // Refresh job details
                if (response.job) {
                    setJobDetails(response.job);
                }
                
                // If user selected "scheduled", redirect to Schedules page to create/manage schedules
                // If "ondemand", redirect to jobs list
                setTimeout(() => {
                    if (formData.type === 'scheduled') {
                        navigate(`/profile/${profileId}/job/${jobId}/schedules`);
                    } else {
                        navigate(`/profile/${profileId}/jobs`);
                    }
                }, 2000);
            } else {
                // Create new job (existing logic - to be implemented with CreateJob API)
                setShowSuccessAlert(true);
                setTimeout(() => {
                    navigate(`/profile/${profileId}/jobs`);
                }, 2000);
            }
        } catch (error: any) {
            console.error('Error saving job:', error);
            setApiError(error.message || 'Failed to save job. Please try again.');
        } finally {
            setSaving(false);
            NProgress.done();
        }
    };
    
    // Handle cancel
    const handleCancel = () => {
        navigate(`/profile/${profileId}/jobs`);
    };
    
    // Handle run job
    const handleRunJob = async () => {
        if (!jobId) {
            console.error('No job ID available');
            return;
        }
        
        setIsRunningJob(true);
        setApiError(null);
        NProgress.start();
        
        try {
            
            const runJobPayload = {
                jobId,
                runType: 'NORMAL'
            };
            
            const response = await makeApiCall(API_PATHS.JOBS.RUN, 'POST', runJobPayload);
            
            // Navigate to job history to see the new run
            const runId = response.runId || null;
            const historyPath = runId
                ? `/profile/${profileId}/job/${jobId}/run/${runId}`
                : `/profile/${profileId}/job/${jobId}/history`;
            navigate(historyPath);
        } catch (error: any) {
            console.error('Error running job:', error);
            setApiError(error.message || 'Failed to run job. Please try again.');
        } finally {
            setIsRunningJob(false);
            NProgress.done();
        }
    };

    // Handle submit for review (CRUX CR creation)
    const handleSubmitForReview = async () => {
        if (!jobId) {
            console.error('No job ID available');
            return;
        }

        setIsSubmittingForReview(true);
        setApiError(null);
        NProgress.start();

        try {
            await makeApiCall(API_PATHS.REVIEW.SUBMIT, 'POST', { jobId });
            // Optimistically hide the button immediately
            setJobDetails((prev: any) => prev ? { ...prev, status: 'APPROVAL_REQUESTED' } : prev);
        } catch (error: any) {
            console.error('Error submitting for review:', error);
            setApiError(error.message || 'Failed to submit for review. Please try again.');
        } finally {
            setIsSubmittingForReview(false);
            NProgress.done();
        }
    };
    
    // Handle file upload
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setFormData({ ...formData, templateFile: file });
            setTemplateUploaded(true);
            setErrors({ ...errors, template: '' });
        }
    };
    
    return (
        <div className="job-create-edit-page" style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <ContentLayout
                defaultPadding={false}
                headerVariant="high-contrast"
                header={
                    <Box padding={{ horizontal: "xxxl", vertical: "l" }}>
                        <SpaceBetween size="m" direction="vertical">
                            <BreadcrumbGroup
                                items={[
                                    { text: "Home", href: "/" },
                                    { text: profileData?.name || 'Loading...', href: `/profile/${profileId}` },
                                    { text: "Jobs", href: `/profile/${profileId}/jobs` },
                                    { text: pageTitle, href: "#" }
                                ]}
                                ariaLabel="Breadcrumbs"
                            />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                                <div>
                                    <Box fontSize="heading-xl" fontWeight="bold" color="inherit">
                                        {pageTitle}
                                    </Box>
                                    <Box fontSize="body-m" color="inherit" margin={{ top: "xs" }}>
                                        {isEditMode && jobDetails ? (
                                            <>
                                                Created: {
                                                    new Date(jobDetails.createdAt).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: '2-digit',
                                                        day: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })
                                                }
                                            </>
                                        ) : (
                                            `Profile: ${profileData?.name || 'Loading...'}`
                                        )}
                                    </Box>
                                </div>
                                {isEditMode && (
                                    <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                                        <Button
                                            iconName="refresh"
                                            variant="normal"
                                            onClick={fetchJobDetails}
                                            ariaLabel="Refresh job details"
                                            loading={loading}
                                        >
                                        
                                        </Button>
                                        <Button
                                            variant='primary'
                                            iconName="play"
                                            onClick={handleRunJob}
                                            loading={isRunningJob}
                                            disabled={(!['ACTIVE', 'RELEASE', 'APPROVAL_REQUESTED', 'APPROVED'].includes(jobDetails?.status || '')) || isFeedbackGenerating}
                                        >
                                            Run Job
                                        </Button>
                                        <Button
                                            variant='normal'
                                            iconName="history"
                                            onClick={() => navigate(`/profile/${profileId}/job/${jobId}/history`)}
                                        >
                                            View Run History
                                        </Button>
                                        <Button
                                            variant='normal'
                                            iconName="calendar"
                                            onClick={() => navigate(`/profile/${profileId}/job/${jobId}/schedules`)}
                                            disabled={(jobDetails?.status !== 'APPROVED' && jobDetails?.status !== 'ACTIVE') || !jobDetails?.complianceSubmitted}
                                        >
                                            Schedules
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </SpaceBetween>
                    </Box>
                }
            >
                <div style={{ height: 'calc(100vh - 200px)', overflowY: 'auto', overflowX: 'hidden' }}>
                <Box padding={{ horizontal: "xxl", vertical: "l" }}>
                    {showSuccessAlert && (
                        <Alert
                            type="success"
                            dismissible
                            onDismiss={() => setShowSuccessAlert(false)}
                            header={isEditMode ? "Job updated successfully" : "Job created successfully"}
                        >
                            {isEditMode && formData.type === 'scheduled'
                                ? 'Redirecting to Schedules page to manage schedules...'
                                : 'Redirecting to jobs list...'}
                        </Alert>
                    )}
                    
                    {apiError && (
                        <Alert
                            type="error"
                            dismissible
                            onDismiss={() => setApiError(null)}
                            header="Error"
                        >
                            {apiError}
                        </Alert>
                    )}
                    
                    {profileError && (
                        <Alert
                            type="warning"
                            dismissible
                            onDismiss={() => {}}
                            header="Profile Error"
                        >
                            {profileError}
                        </Alert>
                    )}
                    
                    {isEditMode && jobDetails?.templateStatus && jobDetails.templateStatus !== 'VALIDATED' && !isFeedbackGenerating && (
                        <Alert
                            type={jobDetails.templateStatus === 'GENERATION_FAILED' ? 'error' : 'info'}
                            header={jobDetails.templateStatus === 'GENERATION_FAILED' ? 'Template Generation Failed' : 'Template Not Ready'}
                        >
                            {jobDetails.templateStatus === 'GENERATION_FAILED' ? (
                                <>
                                    The template generation failed. Please use the feedback section below to provide more specific instructions and try again.
                                    The "Run Job" button is disabled until a valid template is generated.
                                </>
                            ) : (
                                <>
                                    The template for this job is currently in "{jobDetails.templateStatus}" status. 
                                    The "Run Job" button is disabled until the template is validated. 
                                    Please refresh to check for updated status.
                                </>
                            )}
                        </Alert>
                    )}
                  <Box padding={"xs"}> </Box>
                    
                    <SpaceBetween size="l" direction="vertical">
                        {/* Submit for Review Button - Moved to top for visibility */}
                        {isEditMode && jobDetails?.status === 'RELEASE' && (
                            <Alert type="info" header="Action Required: Submit for Review">
                                <SpaceBetween size="s" direction="vertical">
                                    <span>Please review your dataset logic and submit for code review. Schedules will be enabled after the review is approved and the compliance questionnaire is submitted.</span>
                                    <Button
                                        variant="primary"
                                        onClick={handleSubmitForReview}
                                        loading={isSubmittingForReview}
                                        disabled={isSubmittingForReview}
                                    >
                                        Submit for Review
                                    </Button>
                                </SpaceBetween>
                            </Alert>
                        )}

                        {/* Schedule prerequisite note for APPROVAL_REQUESTED status */}
                        {isEditMode && jobDetails?.status === 'APPROVAL_REQUESTED' && (
                            <Alert type="info">
                                Your job has been submitted for code review. Schedules will be enabled once the review is approved and the compliance questionnaire is submitted.
                            </Alert>
                        )}

                        {/* Job Configuration */}
                        <Container
                            header={
                                <Header variant="h2">
                                    Job Configuration
                                </Header>
                            }
                        >
                            <SpaceBetween size="l" direction="vertical">
                                <FormField
                                    label="Name"
                                    errorText={errors.name}
                                    constraintText="Enter a unique name for this job"
                                >
                                    <Input
                                        value={formData.name}
                                        onChange={({ detail }) => {
                                            setFormData({ ...formData, name: detail.value });
                                            setErrors({ ...errors, name: '' });
                                        }}
                                        placeholder="Enter job name"
                                        ariaLabel="Job name"
                                    />
                                </FormField>
                                
                                <FormField
                                    label="Type"
                                    description="Choose how this job will be triggered"
                                >
                                    <RadioGroup
                                        onChange={({ detail }) => 
                                            setFormData({ ...formData, type: detail.value as 'ondemand' | 'scheduled' })
                                        }
                                        value={formData.type}
                                        items={[
                                            { value: 'ondemand', label: 'On demand' },
                                            { value: 'scheduled', label: 'Scheduled', description: 'Run on a recurring schedule' }
                                        ]}
                                    />
                                </FormField>
                                
                                {formData.type === 'scheduled' && (
                                    <Alert type="info">
                                        {isEditMode ? (
                                            <>
                                                Clicking Update will save job details and redirect you to the{' '}
                                                <Link
                                                    onFollow={(e) => {
                                                        e.preventDefault();
                                                        navigate(`/profile/${profileId}/job/${jobId}/schedules`);
                                                    }}
                                                >
                                                    Schedules page
                                                </Link>{' '}
                                                where you can create and manage cron-based schedules (frequency, time, timezone) for this job.
                                            </>
                                        ) : (
                                            'After creating this job, you can set up schedules with cron expressions on the Schedules page.'
                                        )}
                                    </Alert>
                                )}
                                
                                <FormField
                                    label="Description"
                                    description="Optional description for this job"
                                >
                                    <Textarea
                                        value={formData.description || ''}
                                        onChange={({ detail }) => 
                                            setFormData({ ...formData, description: detail.value })
                                        }
                                        placeholder="Enter job description"
                                        rows={3}
                                    />
                                </FormField>
                                
                                {/* <FormField
                                    label="Enable job"
                                    description="Toggle to enable or disable this job"
                                >
                                    <Toggle
                                        onChange={({ detail }) =>
                                            setFormData({ ...formData, enabled: detail.checked })
                                        }
                                        checked={formData.enabled}
                                    >
                                        {formData.enabled ? 'Enabled' : 'Disabled'}
                                    </Toggle>
                                </FormField> */}
                                
                                {/* Form Actions */}
                                <Box float="right">
                                    <SpaceBetween direction="horizontal" size="xs">
                                        <Button onClick={handleCancel}>
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleSubmit}
                                            loading={saving}
                                        >
                                            {isEditMode ? 'Update' : 'Create'}
                                        </Button>
                                    </SpaceBetween>
                                </Box>
                            </SpaceBetween>
                        </Container>
                        
                        {/* Latest Job Run - Show in edit mode when a run exists */}
                        {isEditMode && latestRun && (
                            <Container
                                header={
                                    <Header variant="h2" description="Most recent execution of this job">
                                        Latest Job Run
                                    </Header>
                                }
                            >
                                <ColumnLayout columns={4} variant="text-grid">
                                    <div>
                                        <Box variant="awsui-key-label">Run ID</Box>
                                        <Link
                                            onFollow={(e) => {
                                                e.preventDefault();
                                                navigate(`/profile/${profileId}/job/${jobId}/run/${latestRun.runId}`);
                                            }}
                                        >
                                            {latestRun.runId}
                                        </Link>
                                    </div>
                                    <div>
                                        <Box variant="awsui-key-label">Status</Box>
                                        <StatusIndicator
                                            type={
                                                latestRun.status === 'SUCCEEDED' ? 'success' :
                                                latestRun.status === 'FAILED' ? 'error' :
                                                latestRun.status === 'QUERY_RUNNING' || latestRun.status === 'RUNNING' ? 'in-progress' :
                                                latestRun.status === 'PENDING' ? 'pending' :
                                                'info'
                                            }
                                        >
                                            {latestRun.status}
                                        </StatusIndicator>
                                    </div>
                                    <div>
                                        <Box variant="awsui-key-label">Started</Box>
                                        <Box>{new Date(latestRun.startedAt).toLocaleString()}</Box>
                                    </div>
                                    <div>
                                        <Box variant="awsui-key-label">Completed</Box>
                                        <Box>{latestRun.completedAt ? new Date(latestRun.completedAt).toLocaleString() : '-'}</Box>
                                    </div>
                                </ColumnLayout>
                            </Container>
                        )}

                        {/* Review Status Section - Show when job is in release, review, approved, or active */}
                        {isEditMode && (jobDetails?.status === 'RELEASE' || jobDetails?.status === 'APPROVAL_REQUESTED' || jobDetails?.status === 'APPROVED' || jobDetails?.status === 'ACTIVE') && (
                            <Container
                                header={
                                    <Header variant="h2" description="Review and compliance status for this job">
                                        Review Status
                                    </Header>
                                }
                            >
                                <SpaceBetween size="l">
                                    <Table
                                        columnDefinitions={[
                                            {
                                                id: 'check',
                                                header: 'Check',
                                                cell: (item) => item.check,
                                                width: 200,
                                            },
                                            {
                                                id: 'status',
                                                header: 'Status',
                                                cell: (item) => {
                                                    if (item.status === 'APPROVED') return <StatusIndicator type="success">Approved</StatusIndicator>;
                                                    if (item.status === 'PENDING') return <StatusIndicator type="pending">Pending</StatusIndicator>;
                                                    if (item.status === 'NOT_STARTED') return <StatusIndicator type="stopped">Not Started</StatusIndicator>;
                                                    if (item.status === 'SUBMITTED') return <StatusIndicator type="success">Submitted</StatusIndicator>;
                                                    if (item.status === 'NOT_SUBMITTED') return <StatusIndicator type="warning">Not Submitted</StatusIndicator>;
                                                    if (item.status === 'TBD') return <StatusIndicator type="stopped">TBD</StatusIndicator>;
                                                    return <StatusIndicator type="info">{item.status}</StatusIndicator>;
                                                },
                                                width: 200,
                                            },
                                            {
                                                id: 'detail',
                                                header: 'Detail',
                                                cell: (item) => {
                                                    if (item.link) {
                                                        return <a href={item.link} target="_blank" rel="noopener noreferrer">{item.linkText || 'View'}</a>;
                                                    }
                                                    return item.detail || '-';
                                                },
                                                width: 250,
                                            },
                                        ]}
                                        items={[
                                            {
                                                check: 'CRUX Code Review',
                                                status: (jobDetails?.status === 'APPROVED' || jobDetails?.status === 'ACTIVE') ? 'APPROVED' : jobDetails?.status === 'RELEASE' ? 'NOT_STARTED' : 'PENDING',
                                                link: jobDetails?.crUrl || '',
                                                linkText: jobDetails?.crId || '',
                                                detail: '',
                                            },
                                            {
                                                check: 'SOX Compliance',
                                                status: jobDetails?.complianceSubmitted ? 'SUBMITTED' : 'NOT_SUBMITTED',
                                                link: '',
                                                linkText: '',
                                                detail: jobDetails?.complianceSubmitted
                                                    ? (jobDetails?.isSox ? 'SOX' : 'Non-SOX')
                                                    : 'Questionnaire not yet submitted',
                                            },
                                        ] as Array<{ check: string; status: string; link: string; linkText: string; detail: string }>}
                                        variant="embedded"
                                        wrapLines
                                    />

                                    {/* SOX Compliance Questionnaire or Details */}
                                    {jobDetails?.complianceSubmitted ? (
                                        <ComplianceDetails
                                            isSox={jobDetails.isSox || false}
                                            annualFinancialImpact={jobDetails.annualFinancialImpact || ''}
                                            soxControlTypes={jobDetails.soxControlTypes}
                                            complianceSubmittedBy={jobDetails.complianceSubmittedBy || ''}
                                            complianceSubmittedAt={jobDetails.complianceSubmittedAt || ''}
                                        />
                                    ) : (
                                        <ComplianceQuestionnaireForm
                                            jobId={jobId || ''}
                                            onSubmitSuccess={(isSox) => {
                                                // Refresh job details to get updated compliance fields
                                                fetchJobDetails();
                                            }}
                                        />
                                    )}
                                </SpaceBetween>
                            </Container>
                        )}

                        {/* Feedback Section - Show in edit mode when code has been generated or generation failed */}
                        {isEditMode && (templateUploaded || jobDetails?.generateStatus === 'COMPLETED' || jobDetails?.generateStatus === 'FAILED' || jobDetails?.templateStatus === 'GENERATION_FAILED' || jobDetails?.templateStatus === 'VALIDATED') && jobDetails?.generateStatus !== 'GENERATING' && jobDetails?.generateStatus !== 'SUBMITTED' && (
                            <Container
                                id="feedback-section"
                                header={
                                    <Header
                                        variant="h2"
                                        description="Provide feedback to improve the generated code"
                                        actions={
                                            isFeedbackGenerating && (
                                                <Badge color="blue">
                                                    <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
                                                        <Spinner />
                                                        <span>Generating</span>
                                                    </SpaceBetween>
                                                </Badge>
                                            )
                                        }
                                    >
                                        Code Feedback
                                    </Header>
                                }
                            >
                                <SpaceBetween size="m">
                                    {/* Status Messages */}
                                    {feedbackSuccessMessage && (
                                        <Alert
                                            type="success"
                                            dismissible
                                            onDismiss={clearFeedbackSuccessMessage}
                                            header="Code Updated Successfully"
                                        >
                                            {feedbackSuccessMessage}
                                        </Alert>
                                    )}
                                    
                                    {feedbackError && (
                                        <Alert
                                            type="error"
                                            dismissible
                                            onDismiss={clearFeedbackError}
                                            header="Code Regeneration Failed"
                                        >
                                            <SpaceBetween size="s">
                                                {/* Render error message with proper formatting for newlines and bullets */}
                                                <Box>
                                                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                                        {feedbackError.split('\n').map((line, index) => {
                                                            // Check if line starts with "- " (bullet point)
                                                            if (line.trim().startsWith('- ')) {
                                                                return (
                                                                    <div key={index} style={{ paddingLeft: '16px', marginBottom: '4px' }}>
                                                                        • {line.trim().substring(2)}
                                                                    </div>
                                                                );
                                                            }
                                                            // Check if line ends with ":" (section header)
                                                            if (line.trim().endsWith(':') && line.trim().length > 1) {
                                                                return (
                                                                    <div key={index} style={{ fontWeight: 600, marginTop: index > 0 ? '12px' : '0', marginBottom: '4px' }}>
                                                                        {line}
                                                                    </div>
                                                                );
                                                            }
                                                            // Regular line
                                                            return line.trim() ? (
                                                                <div key={index} style={{ marginBottom: '4px' }}>
                                                                    {line}
                                                                </div>
                                                            ) : (
                                                                <div key={index} style={{ height: '8px' }} />
                                                            );
                                                        })}
                                                    </div>
                                                </Box>
                                            </SpaceBetween>
                                        </Alert>
                                    )}
                                    
                                    {/* Generation Progress */}
                                    {isFeedbackGenerating && (
                                        <Alert
                                            type="info"
                                            header="Code Regeneration in Progress"
                                        >
                                            <SpaceBetween size="xs">
                                                <Box>{getFeedbackStatusMessage(feedbackGenerationStatus)}</Box>
                                                <ProgressBar
                                                    status="in-progress"
                                                    value={generationProgress}
                                                    label={`Processing feedback`}
                                                   
                                                />
                                            </SpaceBetween>
                                        </Alert>
                                    )}
                                    
                                    {/* Feedback Form */}
                                    <FormField
                                        label="Provide Feedback"
                                        description={`Describe what improvements you'd like to see in the generated code (max 300 words, current: ${wordCount})`}
                                        errorText={wordCount > 300 ? 'Feedback exceeds 300 words limit' : ''}
                                        stretch
                                    >
                                        <Textarea
                                            value={feedbackText}
                                            onChange={({ detail }) => setFeedbackText(detail.value)}
                                            placeholder="Example: The template should include additional error handling for edge cases..."
                                            rows={6}
                                            disabled={isFeedbackGenerating || jobDetails?.status === 'APPROVAL_REQUESTED' || jobDetails?.status === 'APPROVED'}
                                            ariaLabel="Feedback text"
                                        />
                                    </FormField>
                                    
                                    {/* Character/Word Counter and Submit Buttons */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box fontSize="body-s" color={wordCount > 300 ? "text-status-error" : "text-label"}>
                                            <strong>{wordCount}/300</strong> words
                                        </Box>
                                        <SpaceBetween direction="horizontal" size="xs">
                                            <Button
                                                variant="link"
                                                onClick={() => {
                                                    setFeedbackText('');
                                                    clearFeedbackError();
                                                    clearFeedbackSuccessMessage();
                                                }}
                                                disabled={isFeedbackGenerating || !feedbackText.trim() || jobDetails?.status === 'APPROVAL_REQUESTED' || jobDetails?.status === 'APPROVED'}
                                            >
                                                Clear
                                            </Button>
                                            <Button
                                                variant="primary"
                                                onClick={submitFeedback}
                                                disabled={!feedbackText.trim() || isFeedbackGenerating || wordCount > 300 || wordCount === 0 || jobDetails?.status === 'APPROVAL_REQUESTED' || jobDetails?.status === 'APPROVED'}
                                                loading={isFeedbackGenerating}
                                                iconName="upload"
                                            >
                                                {isFeedbackGenerating ? 'Regenerating Code...' : 'Submit Feedback'}
                                            </Button>
                                        </SpaceBetween>
                                    </div>
                                    
                                    {/* Information Box */}
                                    <Alert type="info">
                                        <Box>
                                            <strong>How feedback works:</strong>
                                            <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
                                                <li>Your feedback will be used to regenerate improved code</li>
                                                <li>The code regeneration process typically takes a few minutes</li>
                                                <li>Once completed, the job will use the updated code for future runs</li>
                                                <li>You can provide multiple rounds of feedback to refine the output</li>
                                                <li>Run again from this page to use the updated template and get the latest results</li>
                                            </ul>
                                        </Box>
                                    </Alert>
                                </SpaceBetween>
                            </Container>
                        )}
                        
                        {/* Code Artifacts Section */}
                        {/* Show info when template is being generated */}
                        {(jobDetails?.templateStatus === 'SUBMITTED' || jobDetails?.templateStatus === 'GENERATING' || isFeedbackGenerating) && (
                            <Alert
                                type="info"
                                header="Code Generation in Progress"
                            >
                                The code artifacts section is temporarily unavailable while code is being generated. 
                                {isFeedbackGenerating ? 'Your feedback is being processed.' : 'Please refresh the page to check if the generation has completed.'}
                            </Alert>
                        )}
                        
                        {/* Code Artifacts Section - Hide when generating */}
                        {jobDetails?.templateStatus !== 'SUBMITTED' && jobDetails?.templateStatus !== 'GENERATING' && !isFeedbackGenerating && (fetchedMermaidContent || fetchedAiSummary || fetchedSqlContent || jobDetails?.mermaidDiagram || jobDetails?.aiSummary) && (
                        <Container
                            header={
                                <Header variant="h2">
                                    Code Artifacts
                                </Header>
                            }
                        >
                                <Tabs
                                    tabs={[
                                        ...(fetchedSqlContent ? [{
                                            id: 'sql',
                                            label: 'Generated SQL',
                                            content: (
                                                <div style={{
                                                    maxHeight: '500px',
                                                    overflow: 'auto',
                                                    backgroundColor: '#1e1e1e',
                                                    borderRadius: '4px',
                                                    padding: '12px',
                                                    marginTop: '8px'
                                                }}>
                                                    <pre style={{
                                                        color: '#9cdcfe',
                                                        fontFamily: 'Monaco, Menlo, monospace',
                                                        fontSize: '13px',
                                                        margin: 0,
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word'
                                                    }}>
                                                        {fetchedSqlContent}
                                                    </pre>
                                                </div>
                                            )
                                        }] : []),
                                        ...((fetchedAiSummary || jobDetails?.aiSummary) ? [{
                                            id: 'summary',
                                            label: 'AI Summary',
                                            content: (
                                                <div style={{ marginTop: '8px' }}>
                                                    <JsonDataViewer
                                                        data={null}
                                                        aiSummary={fetchedAiSummary || jobDetails?.aiSummary}
                                                        defaultView="summary"
                                                        hideToolbar={true}
                                                        style={{ maxHeight: '500px' }}
                                                    />
                                                </div>
                                            )
                                        }] : []),
                                        ...((fetchedMermaidContent || jobDetails?.mermaidDiagram) ? [{
                                            id: 'mermaid',
                                            label: 'Mermaid Diagram',
                                            content: (
                                                <div style={{ marginTop: '8px' }}>
                                                    <JsonDataViewer
                                                        data={null}
                                                        mermaidDiagram={fetchedMermaidContent || jobDetails?.mermaidDiagram}
                                                        defaultView="mermaid"
                                                        hideToolbar={true}
                                                        style={{ maxHeight: '500px' }}
                                                    />
                                                </div>
                                            )
                                        }] : [])
                                    ]}
                                />
                        </Container>
                        )}

                    </SpaceBetween>
                </Box>
                {/* Add spacing at bottom to prevent footer overlap */}
                <Box padding={{ bottom: "xxxl" }}></Box>
                </div>
            </ContentLayout>
        </div>
    );
};

export default JobCreateEdit;
